'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { ProductActions } from '@eliadmin/core/src/components/dlz/product/product-actions';
import { Badge, StockStatus, type StockTone } from '@eliadmin/core/src/components/dlz/ui/badge';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { useTranslations, type TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';

/** Figma Badge description: at most two badges per card. */
const MAX_BADGES = 2;
const PLACEHOLDER = '/images/placeholder.png';

export type ProductTileLayout = 'vertical' | 'horizontal' | 'compact' | 'grid';

export type ProductTileProps = {
  product: Product;
  /**
   * grid        (default – the core product grids, e.g. the category listing) Figma „Product card“
   *             from `md`; below it the Figma „Mobile / Product card“ of the 2-column phone grids
   *             (60438:86646, 60459:98049): 14 px name in a 64 px box, 40 px cart button, the
   *             favourite over the image
   * vertical    Figma „Product card“ at every width (carousels)
   * horizontal  wide card of „Akciové ponuky“ from `md`, vertical below
   * compact     like horizontal from `md`; below it the Figma „Mobile / Product card“: a row with a
   *             139 × 140 image, the info on the right, an icon-only cart button and no favourites
   */
  layout?: ProductTileLayout;
  imageSizes?: string;
  className?: string;
};

/** Stock line of the card: core availability data in the two DLZ tones. */
function stockStatus(product: Product, t: TranslateFn): { tone: StockTone; label: string } {
  if (product.quantity >= (product.min_quantity || 1)) {
    return { tone: 'in-stock', label: product.availability?.name || t('text2297') };
  }
  if (product.availability?.sellable) {
    return { tone: 'on-order', label: product.availability.name };
  }
  return { tone: 'out-of-stock', label: product.unavailability?.name || t('text495') };
}

function ProductTileContent({ product, layout = 'grid', imageSizes, className }: ProductTileProps) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const { currency, currency_settings: currencySettings } = useGlobalStore(s => s.generalLayout);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const filter = useCategoryStore(s => s.filter);
  const selectedValues = useVariantsStore(s => s.selectedValues);
  const [askForPriceOpen, setAskForPriceOpen] = useState(false);

  const teaser = templateSettings.product_teaser;
  const image = getProductImage(
    templateSettings,
    product,
    getProductFilterVariants(product, filter),
  );
  const href = getProductUrl(product, selectedValues);
  const price = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
    discountDecimalPlaces: 0,
  });
  const unit = templateSettings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';
  const brand = product.manufacturers?.[0]?.name;
  const stock = teaser?.availability?.enabled ? stockStatus(product, t) : null;
  const badges = [
    ...(price.showPreviousPrice
      ? [{ key: 'sale', tone: 'sale' as const, label: `-${price.discountLabel} %` }]
      : []),
    ...(product.tags ?? []).map(tag => ({ key: tag.slug, tone: 'new' as const, label: tag.name })),
  ].slice(0, MAX_BADGES);
  const compact = layout === 'compact';
  const grid = layout === 'grid';
  const horizontal = layout === 'horizontal' || compact;

  return (
    <article
      className={cn(
        // In a product grid the card fills its row by stretching: a percentage height of a grid item
        // resolves differently in Safari when the row is only as high as its content (a lone product).
        'relative flex h-full flex-col gap-3 border-r border-[#E2E4E7] bg-white p-3 md:gap-4 md:p-4 [.ea-product-list>&]:h-auto [.ea-product-list>&]:self-stretch',
        'transition-shadow hover:z-10 hover:rounded-[18px] hover:border-transparent hover:shadow-dropdown',
        horizontal && 'md:flex-row',
        compact && 'flex-row gap-2.5',
        // The card is the size container the overlaid favourite is placed against.
        grid && '@container max-md:gap-2.5',
        className,
      )}
    >
      <div
        className={cn(
          'relative aspect-square w-full shrink-0 overflow-hidden rounded-[18px]',
          horizontal && 'md:w-[45%] md:max-w-[282px] md:self-start',
          compact &&
            'h-[140px] w-[139px] min-w-24 shrink md:aspect-square md:h-auto md:min-w-0 md:shrink-0',
        )}
      >
        <Image
          src={image.url || PLACEHOLDER}
          alt=""
          fill
          sizes={imageSizes ?? 'auto, 300px'}
          unoptimized={!image.url}
          className="object-contain"
        />
        {badges.length ? (
          <div
            className={cn(
              'absolute left-3 top-3 flex gap-1',
              (compact || grid) && 'max-md:left-1.5 max-md:top-1.5',
            )}
          >
            {badges.map(badge => (
              <Badge key={badge.key} tone={badge.tone}>
                {badge.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'flex flex-1 flex-col gap-3 md:gap-4',
          grid && 'max-md:gap-2.5',
          horizontal && 'md:min-w-0',
          compact && 'min-w-0',
        )}
      >
        <div className={cn('flex flex-1 flex-col items-start gap-2', grid && 'max-md:gap-1.5')}>
          {brand ? (
            <p className="dlz-label text-[#6B7079]">
              <span className="dlz-trim block">{brand}</span>
            </p>
          ) : null}
          <h3
            className={cn(
              'dlz-h5 line-clamp-2 max-w-[282px] text-[#0F1012]',
              // Mobile card: 14 px name, 3 px more below it (Figma box of the name is 42 px high).
              compact && 'max-md:mb-[3px] max-md:text-sm max-md:leading-[1.4]',
              // Fixed 64 px box (Figma), so the stock lines of a grid row stay aligned; the link
              // clamps to 3 lines, a clamped box taller than its lines would show the 4th one.
              grid && 'max-md:line-clamp-none max-md:h-16 max-md:text-sm max-md:leading-[1.4]',
            )}
          >
            <Link
              href={href}
              prefetchOnHover
              className={cn(
                'rounded-[18px] after:absolute after:inset-0 after:rounded-[18px] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-[#0F1012]',
                // A clamped box clips its content: square corners keep the first letter whole.
                grid && 'max-md:line-clamp-3 max-md:rounded-none',
              )}
            >
              {product.name}
            </Link>
          </h3>
          {stock ? (
            <StockStatus
              tone={stock.tone}
              className={cn(compact && 'max-md:text-xs max-md:font-bold max-md:leading-none')}
            >
              {stock.label}
            </StockStatus>
          ) : null}
        </div>

        <div
          className={cn(
            'flex flex-wrap items-end justify-between gap-x-2 gap-y-3',
            (compact || grid) && 'max-md:flex-nowrap',
          )}
        >
          {price.priceOnRequest ? (
            <>
              <button
                type="button"
                onClick={() => setAskForPriceOpen(true)}
                className="dlz-body-s-medium relative rounded-sm text-[#D7191F] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
              >
                {t('text2278')}
              </button>
              <AskForPriceDialog
                isOpen={askForPriceOpen}
                setIsOpen={setAskForPriceOpen}
                product={product}
              />
            </>
          ) : (
            <p className="flex flex-col whitespace-nowrap">
              {price.showPreviousPrice ? (
                <del className="dlz-price-old text-[#6B7079]">
                  {formatPrice(price.beforeDiscount, currency, currencySettings)}
                  {unit}
                </del>
              ) : null}
              <span
                className={cn(
                  'dlz-price-l',
                  price.showPreviousPrice ? 'text-[#D7191F]' : 'text-[#0F1012]',
                )}
              >
                {formatPrice(price.current, currency, currencySettings)}
                {unit}
              </span>
              <span className="dlz-caption text-[#6B7079]">
                {price.secondaryIncludesVat ? t('text443') : t('text1122')}
              </span>
            </p>
          )}
          <ProductActions
            product={product}
            labelled={horizontal}
            compact={compact}
            overlay={grid}
            showCart={!!teaser?.cart_button?.enabled}
            showFavorite={!!teaser?.favorite_icon?.enabled}
          />
        </div>
      </div>
    </article>
  );
}

/**
 * DLZ product card. Each card owns its variant state and dialogs like the core card, so the core
 * add-to-cart flow (variant dialog, added-to-cart dialog) works in every listing.
 */
export function ProductTile(props: ProductTileProps) {
  return (
    <VariantsProvider
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductTileContent {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
